<template>
  <span class="ipp-root">
    <span
      v-if="hasPayments"
      :id="targetId"
      class="ipp-value"
      tabindex="0"
      :aria-label="`Payments ${formatCurrency(invoice.amount_paid, invoice.currency_type)} — show payment history`"
    >{{ formatCurrency(invoice.amount_paid, invoice.currency_type) }}</span>
    <span v-else>{{ formatCurrency(invoice.amount_paid, invoice.currency_type) }}</span>

    <!-- LIQ-2075: read-only payment log on hover — amount and date only.
         Recording or editing payments stays in the Superuser's modal. -->
    <b-popover
      v-if="hasPayments"
      :target="targetId"
      triggers="hover focus"
      placement="bottom"
      boundary="window"
      custom-class="ipp-popover"
      @show="load"
    >
      <template #title>Payment history</template>
      <div v-if="loading" class="ipp-muted">Loading…</div>
      <div v-else-if="error" class="ipp-muted">{{ error }}</div>
      <div v-else-if="!payments.length" class="ipp-muted">No payments recorded.</div>
      <table v-else class="ipp-table">
        <thead>
          <tr><th>Date</th><th class="ipp-amount">Amount</th></tr>
        </thead>
        <tbody>
          <tr v-for="p in payments" :key="p.id">
            <td>{{ p.payment_date }}</td>
            <td class="ipp-amount">{{ formatCurrency(p.amount, invoice.currency_type) }}</td>
          </tr>
        </tbody>
      </table>
    </b-popover>
  </span>
</template>

<script>
import { defineComponent, ref, computed, watch } from '@vue/composition-api'
import InvoicingService from '@services/InvoicingService'
import { formatCurrency } from './useInvoiceExport'
import { requestErrorMessage, responseBody } from './useInvoicingSelection'

export default defineComponent({
  name: 'InvoicePaymentsPopover',
  props: {
    invoice: { type: Object, required: true },
  },
  setup(props) {
    const payments = ref([])
    const loading = ref(false)
    const loaded = ref(false)
    const error = ref('')

    const hasPayments = computed(() => Number(props.invoice.amount_paid) > 0)
    const targetId = computed(() => `ipp-${props.invoice.invoice_id}`)

    // Fetched on first hover and kept; a new payment changes amount_paid,
    // which drops the cache so the next hover refetches.
    // A response that lands after the cache was invalidated belongs to the
    // old invoice/amount and is dropped (see the watch below).
    let generation = 0
    const load = async () => {
      if (loaded.value || loading.value) return
      const requested = generation
      loading.value = true
      error.value = ''
      try {
        const resp = await InvoicingService.getInvoicePayments(props.invoice.invoice_id)
        if (requested !== generation) return
        payments.value = responseBody(resp).data || []
        loaded.value = true
      } catch (e) {
        const message = await requestErrorMessage(e, 'The payment history could not be loaded.')
        if (requested === generation) error.value = message
      } finally {
        if (requested === generation) loading.value = false
      }
    }

    watch(() => [props.invoice.invoice_id, props.invoice.amount_paid], () => {
      generation += 1
      loaded.value = false
      loading.value = false
      error.value = ''
      payments.value = []
    })

    return { payments, loading, error, hasPayments, targetId, load, formatCurrency }
  },
})
</script>

<style scoped>
.ipp-value {
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}
.ipp-muted {
  color: #6c757d;
  font-size: 0.85rem;
}
.ipp-table {
  font-size: 0.85rem;
  border-collapse: collapse;
}
.ipp-table th,
.ipp-table td {
  padding: 2px 8px;
  white-space: nowrap;
}
.ipp-table th {
  font-weight: 600;
  border-bottom: 1px solid #dee2e6;
}
.ipp-amount {
  text-align: right;
}
</style>
